import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { EnrollmentPrimaryActionCard } from "@/components/ui/enrollment-primary-action-card";
import { MobileEnrollmentActionBar } from "@/components/ui/mobile-enrollment-action-bar";
import { formatBrDateTime } from "@/utils/br-datetime";
import {
  canPerformEventSelfCheckIn,
  getCheckInWindowState,
  shouldShowEventCheckInPrompt,
} from "@/utils/event-check-in.utils";
import { useEventSelfCheckInFlow } from "@/components/check-in/use-event-self-check-in-flow";
import { cn } from "@/lib/utils";
import { Icon } from "@/components/ui/icon"

type EventCheckInPanelProps = {
  checkedInAt?: string | null;
  eventStartsAt: string;
  eventEndsAt?: string | null;
  hasLocationCoordinates: boolean;
  /** Se true, copy menciona pontos no ranking (somente jogos). */
  countsForRanking?: boolean;
  onCheckIn: (latitude: number, longitude: number) => Promise<{
    checkedInAt: string;
    checkInPoints?: number;
    earlyBonus?: boolean;
  }>;
  onResolveAlreadyCheckedIn?: () => Promise<string | null | undefined>;
  onSuccess?: (checkedInAt: string) => void;
  onSuccessModalChange?: (open: boolean) => void;
  className?: string;
};

export function EventCheckInPanel({
  checkedInAt,
  eventStartsAt,
  eventEndsAt,
  hasLocationCoordinates,
  countsForRanking = false,
  onCheckIn,
  onResolveAlreadyCheckedIn,
  onSuccess,
  onSuccessModalChange,
  className,
}: EventCheckInPanelProps) {
  const [localCheckedInAt, setLocalCheckedInAt] = useState<string | null>(
    checkedInAt ?? null,
  );

  useEffect(() => {
    setLocalCheckedInAt(checkedInAt ?? null);
  }, [checkedInAt]);

  const effectiveCheckedInAt = localCheckedInAt ?? checkedInAt ?? null;

  const { openConfirm, loading, dialogs, successOpen } = useEventSelfCheckInFlow({
    checkedInAt: effectiveCheckedInAt,
    onCheckIn,
    onResolveAlreadyCheckedIn,
    countsForRanking,
    onSuccess: (nextCheckedInAt) => {
      setLocalCheckedInAt(nextCheckedInAt);
      onSuccess?.(nextCheckedInAt);
    },
  });

  useEffect(() => {
    onSuccessModalChange?.(successOpen);
  }, [successOpen, onSuccessModalChange]);

  const windowState = getCheckInWindowState(eventStartsAt, eventEndsAt);
  const isBeforeOpen = windowState.isBeforeOpen;
  const isEarlyBonusPeriod = windowState.isEarlyBonusPeriod;

  const canSubmit =
    canPerformEventSelfCheckIn({
      eventStartsAt,
      eventEndsAt,
      hasLocationCoordinates,
      checkedInAt: effectiveCheckedInAt,
    }) && !loading;

  const showActionPanel =
    shouldShowEventCheckInPrompt({
      eventStartsAt,
      eventEndsAt,
      hasLocationCoordinates,
      checkedInAt: effectiveCheckedInAt,
    }) || loading;

  if (!showActionPanel && !successOpen) return null;

  if (!showActionPanel && successOpen) {
    return <>{dialogs}</>;
  }

  let hint = countsForRanking
    ? "Confirme sua presença no jogo para ganhar pontos no ranking."
    : "Confirme sua presença no local do evento.";

  if (isBeforeOpen) {
    const opensAt = new Date(windowState.opensAtMs).toISOString();
    hint = `Check-in disponível a partir de ${formatBrDateTime(opensAt, {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    })}.`;
  } else if (isEarlyBonusPeriod && countsForRanking) {
    hint = "Faça check-in agora e ganhe pontos extras no ranking!";
  }

  const buttonLabel = loading
    ? "Obtendo localização..."
    : "Fazer check-in";

  const actionButton = (
    <Button
      onClick={openConfirm}
      disabled={!canSubmit}
      loading={loading}
      className="w-full h-12 rounded-xl text-base font-bold shadow-lg shadow-primary/25"
    >
      <Icon name="where_to_vote" />
      {buttonLabel}
    </Button>
  );

  return (
    <>
      {dialogs}
      <EnrollmentPrimaryActionCard
        showActionRequired={canSubmit}
        hint={hint}
        className={cn("hidden lg:block", className)}
      >
        {actionButton}
      </EnrollmentPrimaryActionCard>

      <MobileEnrollmentActionBar
        visible
        showActionRequired={canSubmit}
        buttonLabel={buttonLabel}
        onButtonClick={openConfirm}
        buttonDisabled={!canSubmit}
        buttonIcon="where_to_vote"
        hint={hint}
      />
    </>
  );
}
